import React, { useState, useEffect } from 'react';
import { Button, Box, Typography, Grid, useTheme, useMediaQuery, Dialog, IconButton, TextField } from '@mui/material';
import { signInWithPopup, signInWithEmailAndPassword } from 'firebase/auth';
import { auth, provider } from '../firebase';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import HistoryIcon from '@mui/icons-material/History';
import CloseIcon from '@mui/icons-material/Close';
import OpenInBrowserIcon from '@mui/icons-material/OpenInBrowser';
import * as amplitude from '@amplitude/analytics-browser';

const FeatureItem = ({ icon, title }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3.5 }}>
    <Box sx={{
      width: 44,
      height: 44,
      borderRadius: '12px',
      backgroundColor: 'rgba(255,255,255,0.12)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      mr: 2.5,
    }}>
      {icon}
    </Box>
    <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.9)', fontWeight: 500, lineHeight: 1.4 }}>
      {title}
    </Typography>
  </Box>
);

const Login = () => {
  const [loading, setLoading] = useState(false);
  const [showBrowserPopup, setShowBrowserPopup] = useState(false);
  const [showCorporateForm, setShowCorporateForm] = useState(false);
  const emailInputRef = React.useRef(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [corporateError, setCorporateError] = useState('');
  const [corporateLoading, setCorporateLoading] = useState(false);
  const [browserType, setBrowserType] = useState('');
  const [detectedBrowser, setDetectedBrowser] = useState('');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const detectInAppBrowser = async () => {
    const ua = navigator.userAgent || navigator.vendor || window.opera;

    const isMetaIAB = /FBAN|FBAV|FB_IAB|Instagram/i.test(ua);
    const isTwitter = /Twitter/i.test(ua);
    const isTikTok = /TikTok/i.test(ua);
    const isSnapchat = /Snapchat/i.test(ua);
    const isLine = /Line/i.test(ua);
    const isLinkedIn = /LinkedInApp/i.test(ua);

    const isTelegram =
      typeof window.TelegramWebview !== 'undefined' ||
      (typeof window.TelegramWebviewProxy !== 'undefined' &&
       typeof window.TelegramWebviewProxyProto !== 'undefined') ||
      /Telegram/i.test(ua) ||
      ua.includes('TelegramBot') ||
      /TelegramWebview/i.test(ua) ||
      window.location.search.includes('tgWebAppPlatform') ||
      window.location.search.includes('tgWebAppVersion');

    let isWebView = false;
    if (navigator.userAgentData?.getHighEntropyValues) {
      try {
        const hints = await navigator.userAgentData.getHighEntropyValues(['platform']);
        isWebView = hints.platform === 'Android' && ua.includes('wv');
      } catch (error) {
        console.log('Could not get high entropy values');
      }
    }

    let detectedBrowserType = 'Regular Browser';
    if (isMetaIAB) detectedBrowserType = 'Meta';
    else if (isTwitter) detectedBrowserType = 'Twitter';
    else if (isTikTok) detectedBrowserType = 'TikTok';
    else if (isSnapchat) detectedBrowserType = 'Snapchat';
    else if (isLine) detectedBrowserType = 'Line';
    else if (isLinkedIn) detectedBrowserType = 'LinkedIn';
    else if (isTelegram) detectedBrowserType = 'Telegram';
    else if (isWebView) detectedBrowserType = 'WebView';

    setDetectedBrowser(detectedBrowserType);

    const isInApp = isMetaIAB || isTwitter || isTikTok || isSnapchat || isLine || isLinkedIn || isTelegram || isWebView;

    if (isInApp && isMobile) {
      setBrowserType(detectedBrowserType);
      setShowBrowserPopup(true);
      amplitude.track('ui_login_in_app_browser_detected', {
        browser: detectedBrowserType,
        userAgent: ua
      });
    }
  };

  useEffect(() => {
    detectInAppBrowser();
  }, []);

  const handleGoogleSignIn = async () => {
    try {
      setLoading(true);
      amplitude.track('ui_login_attempted', { method: 'Google', browser: detectedBrowser });
      await signInWithPopup(auth, provider);
      amplitude.track('ui_login_successful', { method: 'Google', browser: detectedBrowser });
    } catch (error) {
      console.error("Error signing in with Google:", error);
      amplitude.track('ui_login_failed', { method: 'Google', browser: detectedBrowser, error: error.message });
    } finally {
      setLoading(false);
    }
  };

  const handleCorporateSignIn = async (e) => {
    e.preventDefault();
    setCorporateError('');
    setCorporateLoading(true);
    amplitude.track('ui_login_attempted', { method: 'Corporate', browser: detectedBrowser });
    try {
      await signInWithEmailAndPassword(auth, email, password);
      amplitude.track('ui_login_successful', { method: 'Corporate', browser: detectedBrowser });
    } catch (error) {
      amplitude.track('ui_login_failed', { method: 'Corporate', browser: detectedBrowser, error: error.message });
      const errorMap = {
        'auth/user-not-found': 'Օգտատերը գոյություն չունի',
        'auth/wrong-password': 'Սխալ գաղտնաբառ',
        'auth/invalid-credential': 'Սխալ էլ․ հասցե կամ գաղտնաբառ',
        'auth/too-many-requests': 'Շատ փորձ: Խնդրում ենք սպասել',
        'auth/user-disabled': 'Հաշիվն անջատված է',
      };
      setCorporateError(errorMap[error.code] || 'Մուտքը ձախողվեց');
    } finally {
      setCorporateLoading(false);
    }
  };

  const handleClosePopup = () => {
    setShowBrowserPopup(false);
    amplitude.track('ui_login_in_app_browser_popup_closed', { browser: detectedBrowser });
  };

  const getBrowserSpecificMessage = () => {
    switch (browserType) {
      case 'Telegram': return 'Telegram-ի միջից գրանցվելը հարմար չէ';
      case 'Meta': return 'Facebook/Instagram-ի միջից գրանցվելը հարմար չէ';
      case 'Twitter': return 'Twitter-ի միջից գրանցվելը հարմար չէ';
      case 'TikTok': return 'TikTok-ի մեջից գրանցվելը հարմար չէ';
      case 'LinkedIn': return 'LinkedIn-ի մեջից գրանցվելը հնարավոր չէ';
      default: return 'Այս հավելվածի մեջից գրանցվելը հարմար չէ';
    }
  };

  const getTelegramSpecificInstruction = () => {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    if (browserType === 'Telegram' && isIOS) {
      return 'Սեղմեք ներքևի աջ անկյունում Safari-ի կոճակը';
    }
    return null;
  };

  return (
    <Box sx={{ position: 'fixed', width: '100%', height: '100%', top: 0, left: 0, overflow: 'auto' }}>
      <Grid container sx={{ minHeight: '100%', alignItems: 'flex-start' }}>

        {/* ── LEFT PANEL ── */}
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            // flex-start + top padding keeps the Google button anchored
            // so the corporate form expanding downward never shifts it
            justifyContent: 'flex-start',
            pt: isMobile ? '10vh' : '16vh',
            pb: 4,
            px: 4,
            backgroundColor: '#fff',
            minHeight: isMobile ? 'auto' : '100vh',
          }}
        >
          {/* Anchored content block */}
          <Box sx={{ maxWidth: 380, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            {/* Logo */}
            <Box
              component="img"
              src="/logo/xosumam_logo_transparent_big.png"
              alt="Xosum.am"
              sx={{ height: 72, mb: 5, objectFit: 'contain' }}
            />

            <Typography variant="h5" fontWeight={700} align="center" sx={{ mb: 0.75, color: '#111827', letterSpacing: '-0.3px' }}>
              Բարի գալուստ
            </Typography>
            <Typography variant="body2" align="center" sx={{ mb: 4, color: '#6b7280' }}>
              Մուտք գործեք ձեր հաշիվ կամ ստեղծեք նորը
            </Typography>

            {/* Google sign-in */}
            <Button
              variant="contained"
              onClick={handleGoogleSignIn}
              fullWidth
              disabled={loading}
              sx={{
                py: 1.5,
                backgroundColor: '#4285F4',
                color: '#fff',
                fontWeight: 600,
                fontSize: '0.95rem',
                textTransform: 'none',
                borderRadius: '10px',
                boxShadow: '0 2px 8px rgba(66,133,244,0.35)',
                '&:hover': {
                  backgroundColor: '#357ae8',
                  boxShadow: '0 4px 16px rgba(66,133,244,0.45)',
                },
                '&.Mui-disabled': { backgroundColor: '#e5e7eb', color: '#9ca3af', boxShadow: 'none' },
              }}
            >
              <img
                src="https://www.gstatic.com/images/branding/product/1x/gsa_64dp.png"
                alt="Google"
                style={{ width: 20, marginRight: 10 }}
              />
              {loading ? 'Մուտք ենք գործում...' : 'Շարունակել Google-ով'}
            </Button>

            {/* Divider */}
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', my: 2.5 }}>
              <Box sx={{ flex: 1, height: '1px', bgcolor: '#f3f4f6' }} />
              <Typography variant="caption" sx={{ px: 2, color: '#d1d5db', fontWeight: 600, letterSpacing: '0.05em' }}>
                ԿԱՄ
              </Typography>
              <Box sx={{ flex: 1, height: '1px', bgcolor: '#f3f4f6' }} />
            </Box>

            {/* Corporate login toggle */}
            <Button
              variant="outlined"
              onClick={() => {
                const opening = !showCorporateForm;
                setShowCorporateForm(v => !v);
                setCorporateError('');
                if (opening) setTimeout(() => emailInputRef.current?.focus(), 0);
              }}
              fullWidth
              sx={{
                py: 1.35,
                borderColor: '#e5e7eb',
                color: '#374151',
                fontWeight: 500,
                fontSize: '0.9rem',
                textTransform: 'none',
                borderRadius: '10px',
                '&:hover': { borderColor: '#9ca3af', backgroundColor: '#fafafa' },
              }}
            >
              Կորպորատիվ մուտք
            </Button>

            {/* Corporate form — expands below without shifting the Google button above */}
            {showCorporateForm && (
              <Box component="form" onSubmit={handleCorporateSignIn} sx={{ width: '100%', mt: 2.5 }}>
                <TextField
                  label="Էլ. հասցե"
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  inputRef={emailInputRef}
                  fullWidth
                  required
                  size="small"
                  sx={{
                    mb: 1.5,
                    '& .MuiOutlinedInput-root': { borderRadius: '10px' },
                  }}
                />
                <TextField
                  label="Գաղտնաբառ"
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  fullWidth
                  required
                  size="small"
                  sx={{
                    mb: 1.5,
                    '& .MuiOutlinedInput-root': { borderRadius: '10px' },
                  }}
                />
                {corporateError && (
                  <Typography variant="body2" color="error" sx={{ mb: 1.5, fontSize: '0.82rem' }}>
                    {corporateError}
                  </Typography>
                )}
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={corporateLoading}
                  sx={{
                    py: 1.35,
                    borderRadius: '10px',
                    fontWeight: 600,
                    fontSize: '0.9rem',
                    textTransform: 'none',
                    backgroundColor: '#111827',
                    boxShadow: 'none',
                    '&:hover': { backgroundColor: '#1f2937', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' },
                    '&.Mui-disabled': { backgroundColor: '#e5e7eb', color: '#9ca3af' },
                  }}
                >
                  {corporateLoading ? 'Մուտք ենք գործում...' : 'Մուտք գործել'}
                </Button>
              </Box>
            )}
          </Box>

          {/* Privacy policy — anchored to bottom of panel */}
          <Box sx={{ mt: 'auto', pt: 5, textAlign: 'center', maxWidth: 380, width: '100%' }}>
            <Typography variant="caption" sx={{ color: '#9ca3af', lineHeight: 1.8 }}>
              Մուտք գործելով՝ դուք համաձայնում եք մեր{' '}
              <Box
                component="a"
                href="https://xosum.am/terms"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ color: '#4285F4', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
              >
                Պայմաններին
              </Box>
              {' '}ու{' '}
              <Box
                component="a"
                href="https://xosum.am/privacy"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ color: '#4285F4', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
              >
                Գաղտնիության քաղաքականությանը
              </Box>
            </Typography>
          </Box>
        </Grid>

        {/* ── RIGHT PANEL ── */}
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            background: 'linear-gradient(150deg, #0f172a 0%, #1e3a6e 55%, #2563eb 100%)',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            p: isMobile ? 4 : 6,
            minHeight: isMobile ? '45vh' : '100vh',
            // sticky keeps the panel fixed to the viewport top while the left panel grows
            position: isMobile ? 'relative' : 'sticky',
            top: 0,
            height: isMobile ? 'auto' : '100vh',
            overflow: 'hidden',
            // Subtle decorative circles
            '&::before': {
              content: '""',
              position: 'absolute',
              width: 400,
              height: 400,
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.03)',
              top: -100,
              right: -100,
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              width: 300,
              height: 300,
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.04)',
              bottom: -80,
              left: -80,
            },
          }}
        >
          <Box sx={{ maxWidth: 420, position: 'relative', zIndex: 1 }}>
            <Typography
              variant={isMobile ? 'h5' : 'h4'}
              sx={{ color: '#fff', fontWeight: 700, mb: 1.5, letterSpacing: '-0.5px', lineHeight: 1.25 }}
            >
              Ինչու՞ օգտագործել Xosum.am
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: 'rgba(255,255,255,0.55)', mb: isMobile ? 3 : 5, lineHeight: 1.7 }}
            >
              Արագ, հեշտ և հուսալի ձայնի վերծանում
            </Typography>

            <FeatureItem
              icon={<CloudUploadIcon sx={{ color: '#fff', fontSize: 22 }} />}
              title="Վերբեռնեք և վերծանեք մեծ ֆայլեր"
            />
            <FeatureItem
              icon={<PhoneIphoneIcon sx={{ color: '#fff', fontSize: 22 }} />}
              title="Օգտագործեք բջջային սարքերում"
            />
            <FeatureItem
              icon={<HistoryIcon sx={{ color: '#fff', fontSize: 22 }} />}
              title="Տեսեք ձեր ձայնագրությունների պատմությունը"
            />
          </Box>
        </Grid>
      </Grid>

      {/* ── In-app browser warning dialog ── */}
      <Dialog
        open={showBrowserPopup}
        onClose={handleClosePopup}
        maxWidth="sm"
        fullWidth
        sx={{ '& .MuiDialog-paper': { borderRadius: 3, mx: 2 } }}
      >
        <Box sx={{ p: 3, textAlign: 'center', position: 'relative' }}>
          <IconButton
            onClick={handleClosePopup}
            sx={{ position: 'absolute', top: 8, right: 8, color: '#666' }}
          >
            <CloseIcon />
          </IconButton>
          <Box sx={{ mb: 3, mt: 2 }}>
            <OpenInBrowserIcon sx={{ fontSize: 64, color: '#4285F4', mb: 2 }} />
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#333' }}>
              {getBrowserSpecificMessage()}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Բացեք արտաքին բրաուզերում հեշտ մուտք գործելու համար
            </Typography>
            {getTelegramSpecificInstruction() && (
              <Typography variant="body2" sx={{
                color: '#ff9800',
                fontWeight: 'bold',
                backgroundColor: '#fff3e0',
                p: 1,
                borderRadius: 1,
                mb: 2,
              }}>
                {getTelegramSpecificInstruction()}
              </Typography>
            )}
          </Box>
        </Box>
      </Dialog>
    </Box>
  );
};

export default Login;
